'use client';

import React from 'react';
import { Card } from '@tremor/react';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { MultiLineTick, formatWeekLabel } from './StackBarChart';
import { MonthYearTick } from './components/MonthYearTick';
import { DEFAULT_COLORS } from '@/constants/colors';
import { IMetricData } from '@/modules/configs/types';
import { NumberUtils } from '@/utils/NumberUtils';
import { NoDataView } from '../NoDataView';
import { useContainerWidth } from '@/hooks/useContainerWidth';

export interface StackedBarLineData {
    metrics: IMetricData[];
    data: {
        segment: string;
        [key: string]: string | number;
    }[];
    error?: string;
}

interface StackedBarLineProps {
    data?: StackedBarLineData;
    className?: string;
    valueFormatter?: (value: number) => string;
    yAxisWidth?: number;
    colors?: string[];
    segment?: string;
    lineMetricLabel?: string;
    simpleBarLine?: boolean;
    tripleStack?: boolean;
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: ReadonlyArray<{
        name?: string | number;
        value?: unknown;
        color?: string;
        dataKey?: string;
        type?: string;
    }>;
    label?: string;
    valueFormatter?: (value: number) => string;
    percentageDataKey?: string;
}

const CustomTooltip = ({ active, payload, label, valueFormatter, percentageDataKey }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white px-3 py-2 border border-gray-200 shadow-lg rounded-md">
                <p className="font-medium text-gray-900 text-sm mb-2">{label}</p>
                {payload.map((entry, index) => {
                    const raw = entry.value;
                    const numValue = typeof raw === "number" ? raw : Number(raw);
                    const hasNum = Number.isFinite(numValue);
                    const isPercent = !!percentageDataKey && entry.dataKey === percentageDataKey;
                    const formattedValue = hasNum
                        ? (isPercent ? NumberUtils.formatPercent(numValue) : (valueFormatter ? valueFormatter(numValue) : numValue))
                        : (raw == null ? "" : String(raw));

                    return (
                        <div key={index} className="flex items-center gap-2 mb-1">
                            <div
                                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                                style={{ backgroundColor: entry.color ?? "#5a717a" }}
                            />
                            <span className="text-sm text-gray-700">{entry.name ?? ""}:</span>
                            <span className="font-semibold text-sm text-gray-900">
                                {formattedValue}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

function defaultValueFormatter(number: number) {
    const formatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
        notation: 'compact',
        compactDisplay: 'short',
    });
    return formatter.format(number);
}

function compactNumberFormatter(number: number): string {
    if (number >= 1000000000) {
        return (number / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (number >= 1000000) {
        return (number / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (number >= 1000) {
        return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return number.toString();
}

const AngledTick = ({ x, y, payload, fontSize }: { x: string | number; y: string | number; payload: { value: any }; fontSize: number }) => {
    const value = payload.value;
    const valueStr = value !== null && value !== undefined ? String(value) : '';
    const displayText = valueStr.length > 15 ? valueStr.substring(0, 15) + '...' : valueStr;

    return (
        <g transform={`translate(${x},${y})`}>
            <title>{valueStr}</title>
            <text
                x={0}
                y={0}
                dy={8}
                textAnchor="end"
                fill="#212121"
                fontSize={fontSize}
                fontFamily="Roboto, sans-serif"
                transform="rotate(-45)"
            >
                {displayText}
            </text>
        </g>
    );
};

export function StackedBarLine({ data, className, valueFormatter, yAxisWidth, colors, segment, lineMetricLabel, simpleBarLine, tripleStack }: StackedBarLineProps) {
    const { containerWidth, containerRef } = useContainerWidth();
    const dataPointCount = data?.data?.length || 0;
    const shouldAngleLabels = dataPointCount > 8;

    // Debug logging
    console.log('StackedBarLine Debug:', {
        containerWidth,
        dataPointCount,
        shouldAngleLabels
    });

    if (data?.error) {
        return <div className="py-5 px-10 text-center text-red-500">Error: {data.error}</div>;
    }

    if (!data || !data.data || data.data.length === 0 || !data.metrics || data.metrics.length === 0) {
        return <NoDataView />;
    }

    // Check if all data values are 0, null, undefined, or non-numeric (excluding segment key)
    const hasMeaningfulData = data.data.some(item =>
        Object.entries(item).some(([key, value]) =>
            key !== 'segment' && value != null && value !== 0 && typeof value === 'number'
        )
    );

    if (!hasMeaningfulData) {
        return <NoDataView />;
    }

    if (
        simpleBarLine
            ? data.metrics.length < 2
            : tripleStack
                ? data.metrics.length !== 3
                : (data.metrics.length < 2 || data.metrics.length > 3)
    ) {
        return (
            <div className="py-5 px-10 text-center text-gray-500">
                {tripleStack
                    ? 'Triple stack chart requires exactly 3 metrics.'
                    : 'Stacked Bar + Line chart requires 2 or 3 metrics.'}
            </div>
        );
    }

    const colorsToUse = colors && colors.length > 0 ? colors : DEFAULT_COLORS;

    // simple-bar-line mode: first metric is a single bar, second is the line
    if (simpleBarLine) {
        const barMetric = data.metrics[0];
        const lineMetricDef = { ...data.metrics[1], showName: lineMetricLabel ?? data.metrics[1].showName };
        const barColor = colorsToUse[0];
        const lineColor = colorsToUse[1];
        const isPercentLine = lineMetricDef.metricType === 'percent';
        const lineValues = data.data.map(item => Number(item[lineMetricDef.metricName]) || 0);
        const lineDomain: [number, number] = isPercentLine ? [0, 100] : [Math.min(0, Math.floor(Math.min(...lineValues) * 0.9)), Math.ceil(Math.max(...lineValues) * 1.1)];
        const rightFmt = isPercentLine ? (v: number) => `${v}%` : (v: number) => defaultValueFormatter(v);

        const chartContent = (mobile: boolean) => (
            <ComposedChart
                data={data.data}
                margin={{ top: 20, right: mobile ? 50 : 70, left: mobile ? 45 : 60, bottom: shouldAngleLabels ? (mobile ? 30 : 20) : 10 }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical horizontal stroke="#e5e7eb" />
                <XAxis
                    dataKey="segment"
                    height={shouldAngleLabels ? (mobile ? 60 : 80) : 60}
                    tick={shouldAngleLabels ? (props) => <AngledTick {...props} fontSize={mobile ? 9 : 9} /> : (props) => {
                        if (segment === 'month') return <MonthYearTick {...props} fontSize={mobile ? 9 : 12} lineHeight={mobile ? 12 : 14} />;
                        const isWeek = segment === 'week';
                        const tickProps = isWeek ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } } : props;
                        return <MultiLineTick {...tickProps} maxCharsPerLine={isWeek ? 30 : 12} maxLines={2} fontSize={mobile ? 9 : 12} lineHeight={mobile ? 12 : 14} />;
                    }}
                    interval={0} tickMargin={10}
                    axisLine={{ stroke: '#e5e7eb' }} tickLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis yAxisId="left"
                    tick={{ fill: '#5a717a', fontSize: mobile ? 10 : 12, fontFamily: 'Roboto, sans-serif' }}
                    axisLine={{ stroke: '#e5e7eb' }} tickLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={mobile ? compactNumberFormatter : valueFormatter}
                    tickCount={mobile ? 5 : 6}
                    label={{ value: barMetric.showName, angle: -90, position: 'insideLeft', dx: mobile ? -25 : -30, style: { fontSize: mobile ? 10 : 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' } }}
                />
                <YAxis yAxisId="right" orientation="right"
                    tick={{ fill: '#5a717a', fontSize: mobile ? 10 : 12, fontFamily: 'Roboto, sans-serif' }}
                    axisLine={{ stroke: '#e5e7eb' }} tickLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={rightFmt} domain={lineDomain} tickCount={mobile ? 5 : 6} allowDecimals={false}
                    label={{ value: lineMetricDef.showName, angle: 90, position: 'insideRight', dx: mobile ? 25 : 30, style: { fontSize: mobile ? 10 : 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' } }}
                />
                <Tooltip cursor={false} content={(props) => (
                    <CustomTooltip active={props.active} payload={props.payload as any} label={props.label ? String(props.label) : undefined} valueFormatter={valueFormatter} percentageDataKey={isPercentLine ? lineMetricDef.metricName : undefined} />
                )} />
                <Bar dataKey={barMetric.metricName} name={barMetric.showName} fill={barColor} yAxisId="left" isAnimationActive animationDuration={1000} animationEasing="ease-out" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey={lineMetricDef.metricName} name={lineMetricDef.showName} stroke={lineColor} strokeWidth={mobile ? 2 : 3} yAxisId="right" dot={{ fill: lineColor, r: mobile ? 3 : 4 }} activeDot={{ r: mobile ? 5 : 6 }} isAnimationActive animationDuration={1000} animationEasing="ease-out" />
            </ComposedChart>
        );

        return (
            <div className="w-full h-full flex flex-col overflow-hidden" ref={containerRef}>
                <Card className={`w-full mx-auto pt-1 px-6 pb-6 flex flex-col justify-between ${className || ''}`}>
                    <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-6 ml-2">
                        <li className="flex gap-2 items-center font-normal">
                            <span className="w-3 h-3 rounded-[3px] inline-block" style={{ backgroundColor: barColor }} />
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{barMetric.showName}</span>
                        </li>
                        <li className="flex gap-2 items-center font-normal">
                            <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0">
                                <line x1="0" y1="6" x2="12" y2="6" stroke={lineColor} strokeWidth="2" />
                            </svg>
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{lineMetricDef.showName}</span>
                        </li>
                    </ul>
                    <div className="flex flex-col lg:flex-row overflow-hidden">
                        <div className="w-full overflow-hidden">
                            <div className="hidden h-96 sm:block">
                                <ResponsiveContainer width="100%" height="100%">{chartContent(false)}</ResponsiveContainer>
                            </div>
                            <div className="h-64 sm:hidden">
                                <ResponsiveContainer width="100%" height="100%">{chartContent(true)}</ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    // ── Triple-stack mode: all 3 metrics as stacked bars, no line ──
    if (tripleStack) {
        const allBarMetrics = data.metrics;
        const allBarColors = [colorsToUse[0], colorsToUse[1], colorsToUse[2]];

        const tripleChartContent = (mobile: boolean) => (
            <ComposedChart
                data={data.data}
                margin={{ top: 20, right: mobile ? 20 : 30, left: mobile ? 45 : 60, bottom: shouldAngleLabels ? (mobile ? 30 : 20) : 10 }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical horizontal stroke="#e5e7eb" />
                <XAxis
                    dataKey="segment"
                    height={shouldAngleLabels ? (mobile ? 60 : 80) : 60}
                    tick={shouldAngleLabels ? (props) => <AngledTick {...props} fontSize={mobile ? 9 : 9} /> : (props) => {
                        if (segment === 'month') return <MonthYearTick {...props} fontSize={mobile ? 9 : 12} lineHeight={mobile ? 12 : 14} />;
                        const isWeek = segment === 'week';
                        const tickProps = isWeek ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } } : props;
                        return <MultiLineTick {...tickProps} maxCharsPerLine={isWeek ? 30 : 12} maxLines={2} fontSize={mobile ? 9 : 12} lineHeight={mobile ? 12 : 14} />;
                    }}
                    interval={0} tickMargin={10}
                    axisLine={{ stroke: '#e5e7eb' }} tickLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                    yAxisId="left"
                    tick={{ fill: '#5a717a', fontSize: mobile ? 10 : 12, fontFamily: 'Roboto, sans-serif' }}
                    axisLine={{ stroke: '#e5e7eb' }} tickLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={mobile ? compactNumberFormatter : valueFormatter}
                    tickCount={mobile ? 5 : 6}
                    label={{ value: `${allBarMetrics[0].showName} / ${allBarMetrics[1].showName} / ${allBarMetrics[2].showName}`, angle: -90, position: 'insideLeft', dx: mobile ? -25 : -30, style: { fontSize: mobile ? 10 : 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' } }}
                />
                <Tooltip cursor={false} content={(props) => (
                    <CustomTooltip active={props.active} payload={props.payload as any} label={props.label ? String(props.label) : undefined} valueFormatter={valueFormatter} />
                )} />
                {allBarMetrics.map((metric, idx) => (
                    <Bar
                        key={metric.metricName}
                        dataKey={metric.metricName}
                        name={metric.showName}
                        fill={allBarColors[idx]}
                        stackId="stack"
                        yAxisId="left"
                        isAnimationActive animationDuration={1000} animationEasing="ease-out"
                        radius={idx === allBarMetrics.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                ))}
            </ComposedChart>
        );

        return (
            <div className="w-full h-full flex flex-col overflow-hidden" ref={containerRef}>
                <Card className={`w-full mx-auto pt-1 px-6 pb-6 flex flex-col justify-between ${className || ''}`}>
                    <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-6 ml-2">
                        {allBarMetrics.map((metric, i) => (
                            <li key={metric.metricName} className="flex gap-2 items-center font-normal">
                                <span className="w-3 h-3 rounded-[3px] inline-block" style={{ backgroundColor: allBarColors[i] }} />
                                <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{metric.showName}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="flex flex-col lg:flex-row overflow-hidden">
                        <div className="w-full overflow-hidden">
                            <div className="hidden h-96 sm:block">
                                <ResponsiveContainer width="100%" height="100%">{tripleChartContent(false)}</ResponsiveContainer>
                            </div>
                            <div className="h-64 sm:hidden">
                                <ResponsiveContainer width="100%" height="100%">{tripleChartContent(true)}</ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    // Handle 2 metrics case: both are bars, create synthetic line metric for percentage
    const hasTwoMetrics = data.metrics.length === 2;
    const barMetrics = hasTwoMetrics ? data.metrics : data.metrics.slice(0, 2);
    const lineMetric = hasTwoMetrics
        ? {
            metricName: '__calculated_percentage__',
            showName: lineMetricLabel ?? 'Percentage',
            type: 'metric' as const,
            services: [] as any,
            metricType: 'percent' as const,
            category: 'performance' as const
          }
        : { ...data.metrics[2], showName: lineMetricLabel ?? data.metrics[2].showName };

    const barColors = [colorsToUse[0], colorsToUse[1]];
    const lineColor = colorsToUse[2];

    // Check if line metric is all zeros and calculate fallback percentage
    const lineMetricValues = hasTwoMetrics 
        ? [0]
        : data.data.map(item => Number(item[lineMetric.metricName]) || 0);
    const isLineMetricAllZeros = lineMetricValues.every(val => val === 0);

    // Transform data to add calculated percentage if needed
    const transformedData = data.data.map(item => {
        const transformed = { ...item };
        
        if (hasTwoMetrics || isLineMetricAllZeros) {
            // Get values from first 2 metrics
            const firstMetricValue = Number(item[barMetrics[0].metricName]) || 0;
            const secondMetricValue = Number(item[barMetrics[1].metricName]) || 0;
            
            // Calculate percentage: (smallest / largest) * 100
            const minValue = Math.min(firstMetricValue, secondMetricValue);
            const maxValue = Math.max(firstMetricValue, secondMetricValue);
            
            // Avoid division by zero
            const calculatedPercentage = maxValue > 0 
                ? (minValue / maxValue) * 100 
                : 0;
            
            // Format to 2 decimal places
            transformed[lineMetric.metricName] = Number(calculatedPercentage.toFixed(2));
        }
        
        return transformed;
    });

    // Determine if line metric is a percentage/rate type
    const isPercentageMetric = lineMetric.metricType === 'percent' || hasTwoMetrics || isLineMetricAllZeros;
    
    // Calculate dynamic domain for non-percentage metrics
    const lineValues = transformedData.map(item => Number(item[lineMetric.metricName]) || 0);
    const minLineValue = Math.min(...lineValues);
    const maxLineValue = Math.max(...lineValues);
    const lineDomain = isPercentageMetric ? [0, 100] : [Math.min(0, Math.floor(minLineValue * 0.9)), Math.ceil(maxLineValue * 1.1)];
    
    // Formatter for right axis
    const rightAxisFormatter = isPercentageMetric 
        ? (value: number) => `${value}%`
        : (value: number) => defaultValueFormatter(value);

    return (
        <div className="w-full h-full flex flex-col overflow-hidden" ref={containerRef}>
            <Card className={`w-full mx-auto pt-1 px-6 pb-6 flex flex-col justify-between ${className || ''}`}>
                {/* Custom Legend */}
                <ul className="flex justify-start gap-5 text-primary-blue list-none p-0 mb-6 ml-2">
                    {barMetrics.map((metric, i) => (
                        <li key={metric.metricName} className="flex gap-2 items-center font-normal">
                            <span
                                className="w-3 h-3 rounded-[3px] inline-block"
                                style={{ backgroundColor: barColors[i] }}
                            />
                            <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{metric.showName}</span>
                        </li>
                    ))}
                    <li key={lineMetric.metricName} className="flex gap-2 items-center font-normal">
                        <svg width="12" height="12" viewBox="0 0 12 12" className="flex-shrink-0">
                            <line x1="0" y1="6" x2="12" y2="6" stroke={lineColor} strokeWidth="2" />
                        </svg>
                        <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>{lineMetric.showName}</span>
                    </li>
                </ul>

                <div className="flex flex-col lg:flex-row overflow-hidden">
                    <div className="w-full overflow-hidden">
                        <div className="hidden h-96 sm:block">
                            <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={transformedData}
                                margin={{
                                    top: 20,
                                    right: 70,
                                    left: 60,
                                    bottom: shouldAngleLabels ? 20 : 10,
                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={true}
                                    horizontal={true}
                                    stroke="#e5e7eb"
                                />
                                <XAxis
                                    dataKey="segment"
                                    height={shouldAngleLabels ? 80 : 60}
                                    tick={shouldAngleLabels ? (props) => (
                                        <AngledTick
                                            {...props}
                                            fontSize={9}
                                        />
                                    ) : (props) => {
                                        if (segment === 'month') {
                                            return (
                                                <MonthYearTick
                                                    {...props}
                                                    fontSize={12}
                                                    lineHeight={14}
                                                />
                                            );
                                        }
                                        const isWeek = segment === 'week';
                                        const tickProps = isWeek
                                            ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } }
                                            : props;
                                        return (
                                            <MultiLineTick
                                                {...tickProps}
                                                maxCharsPerLine={isWeek ? 30 : 12}
                                                maxLines={2}
                                                fontSize={12}
                                                lineHeight={14}
                                            />
                                        );
                                    }}
                                    interval={0}
                                    tickMargin={10}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                />

                                <YAxis
                                    yAxisId="left"
                                    tick={{ fill: '#5a717a', fontSize: 12, fontFamily: 'Roboto, sans-serif' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                    tickFormatter={valueFormatter}
                                    tickCount={6}
                                    label={{ 
                                        value: `${barMetrics[0].showName} & ${barMetrics[1].showName}`, 
                                        angle: -90, 
                                        position: 'insideLeft',
                                        dx: -30,
                                        style: { fontSize: 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                                    }}
                                />

                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tick={{ fill: '#5a717a', fontSize: 12, fontFamily: 'Roboto, sans-serif' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                    tickFormatter={rightAxisFormatter}
                                    domain={lineDomain}
                                    tickCount={6}
                                    allowDecimals={false}
                                    label={{
                                        value: lineMetric.showName,
                                        angle: 90, 
                                        position: 'insideRight',
                                        dx: 30,
                                        style: { fontSize: 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                                    }}
                                />

                                <Tooltip
                                    cursor={false}
                                    content={(props) => (
                                        <CustomTooltip
                                            active={props.active}
                                            payload={props.payload as any}
                                            label={props.label ? String(props.label) : undefined}
                                            valueFormatter={valueFormatter}
                                            percentageDataKey={isPercentageMetric ? lineMetric.metricName : undefined}
                                        />
                                    )}
                                />


                                {barMetrics.map((metric: IMetricData, idx: number) => (
                                    <Bar
                                        key={metric.metricName}
                                        dataKey={metric.metricName}
                                        name={metric.showName}
                                        fill={barColors[idx]}
                                        stackId="stack"
                                        yAxisId="left"
                                        isAnimationActive={true}
                                        animationDuration={1000}
                                        animationEasing="ease-out"
                                        radius={idx === barMetrics.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                    />
                                ))}

                                <Line
                                    type="monotone"
                                    dataKey={lineMetric.metricName}
                                    name={lineMetric.showName}
                                    stroke={lineColor}
                                    strokeWidth={3}
                                    yAxisId="right"
                                    dot={{ fill: lineColor, r: 4 }}
                                    activeDot={{ r: 6 }}
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="h-64 sm:hidden">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart
                                data={transformedData}
                                margin={{
                                    top: 20,
                                    right: 50,
                                    left: 45,
                                    bottom: 30,
                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    vertical={true}
                                    horizontal={true}
                                    stroke="#e5e7eb"
                                />

                                <XAxis
                                    dataKey="segment"
                                    tick={(props) => {
                                        if (segment === 'month') {
                                            return (
                                                <MonthYearTick
                                                    {...props}
                                                    fontSize={9}
                                                    lineHeight={12}
                                                />
                                            );
                                        }
                                        return (
                                            <AngledTick
                                                {...props}
                                                fontSize={9}
                                            />
                                        );
                                    }}
                                    interval={0}
                                    tickMargin={10}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                    height={60}
                                />

                                <YAxis
                                    yAxisId="left"
                                    tick={{ fill: '#5a717a', fontSize: 10, fontFamily: 'Roboto, sans-serif' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                    tickFormatter={compactNumberFormatter}
                                    tickCount={5}
                                    label={{
                                        value: `${barMetrics[0].showName} & ${barMetrics[1].showName}`,
                                        angle: -90,
                                        position: 'insideLeft',
                                        dx: -25,
                                        style: { fontSize: 10, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                                    }}
                                />

                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tick={{ fill: '#5a717a', fontSize: 10, fontFamily: 'Roboto, sans-serif' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={{ stroke: '#e5e7eb' }}
                                    tickFormatter={rightAxisFormatter}
                                    domain={lineDomain}
                                    tickCount={5}
                                    allowDecimals={false}
                                    label={{
                                        value: lineMetric.showName,
                                        angle: 90,
                                        position: 'insideRight',
                                        dx: 25,
                                        style: { fontSize: 10, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                                    }}
                                />

                                <Tooltip
                                    cursor={false}
                                    content={(props) => (
                                        <CustomTooltip
                                            active={props.active}
                                            payload={props.payload as any}
                                            label={props.label ? String(props.label) : undefined}
                                            valueFormatter={valueFormatter}
                                            percentageDataKey={isPercentageMetric ? lineMetric.metricName : undefined}
                                        />
                                    )}
                                />

                                {barMetrics.map((metric: IMetricData, idx: number) => (
                                    <Bar
                                        key={metric.metricName}
                                        dataKey={metric.metricName}
                                        name={metric.showName}
                                        fill={barColors[idx]}
                                        stackId="stack"
                                        yAxisId="left"
                                        isAnimationActive={true}
                                        animationDuration={1000}
                                        animationEasing="ease-out"
                                        radius={idx === barMetrics.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                    />
                                ))}

                                <Line
                                    type="monotone"
                                    dataKey={lineMetric.metricName}
                                    name={lineMetric.showName}
                                    stroke={lineColor}
                                    strokeWidth={2}
                                    yAxisId="right"
                                    dot={{ fill: lineColor, r: 3 }}
                                    activeDot={{ r: 5 }}
                                    isAnimationActive={true}
                                    animationDuration={1000}
                                    animationEasing="ease-out"
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </Card>
        </div>
    );
}
